import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/design";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiFetch } from "@/lib/api";

interface SecurityEvent {
  id: string;
  reason: string | null;
  createdAt: string;
  actorName: string | null;
  detail: Record<string, unknown> | null;
}

interface DocumentSecuritySettings {
  workerDocumentsEnabled: boolean;
  siteManagerFieldDocumentVisibility: boolean;
  auditorDocumentVisibility: boolean;
  requireDownloadReasonForSensitiveDocs: boolean;
  expirationWarningDays: number;
  maxUploadSizeBytes: number;
  retentionTemplatesEnabled: boolean;
  recentSensitiveDownloads: SecurityEvent[];
  recentAccessDenied: SecurityEvent[];
}

export function DocumentSecuritySettingsPage() {
  const [settings, setSettings] = useState<DocumentSecuritySettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ data: DocumentSecuritySettings }>("/api/settings/security/documents")
      .then((response) => setSettings(response.data))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load document security settings");
      });
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Document security"
        description="Worker-document access, retention, and audit controls."
      />

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
        Retention templates are operational helpers. Your company is responsible for confirming
        legal retention requirements. Werkio is not legal, tax, immigration, HR, or labor
        compliance advice.
      </div>

      {error ? <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm">{error}</p> : null}
      {!settings && !error ? <div className="h-40 animate-pulse rounded-xl bg-muted" /> : null}

      {settings ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4" />
                Controls
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2">
              <Setting label="Worker documents enabled" value={settings.workerDocumentsEnabled} />
              <Setting
                label="Site manager field-document visibility"
                value={settings.siteManagerFieldDocumentVisibility}
              />
              <Setting
                label="Auditor document visibility"
                value={settings.auditorDocumentVisibility}
              />
              <Setting
                label="Sensitive download reason required"
                value={settings.requireDownloadReasonForSensitiveDocs}
              />
              <Setting
                label="Retention templates enabled"
                value={settings.retentionTemplatesEnabled}
              />
              <TextSetting
                label="Expiration warning"
                value={`${settings.expirationWarningDays} days`}
              />
              <TextSetting
                label="Maximum upload size"
                value={`${Math.round(settings.maxUploadSizeBytes / 1024 / 1024)} MB`}
              />
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <EventCard title="Recent sensitive downloads" events={settings.recentSensitiveDownloads} />
            <EventCard title="Recent access-denied events" events={settings.recentAccessDenied} />
          </div>
        </>
      ) : null}
    </div>
  );
}

function Setting({ label, value }: { label: string; value: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
      <span>{label}</span>
      <Badge variant={value ? "default" : "outline"}>{value ? "Enabled" : "Disabled"}</Badge>
    </div>
  );
}

function TextSetting({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
      <span>{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function EventCard({ title, events }: { title: string; events: SecurityEvent[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent events.</p>
        ) : (
          <ul className="space-y-3">
            {events.map((event) => (
              <li key={event.id} className="border-b pb-3 text-sm last:border-0">
                <p className="font-medium">{event.actorName ?? "System"}</p>
                <p className="text-muted-foreground">{event.reason ?? "No reason recorded"}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(event.createdAt).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
